/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: RecordingTtsModal.tsx
 🧩 Description:
    Modal for creating or editing voice recordings (TTS + file upload).
    TTS section  → RecordingTtsModeSection
    Upload section → RecordingUploadModeSection
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useRef } from 'react';
import { Bot, Upload, X, Loader2, Mic } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import {
    createRecordingFromFile, createRecordingFromTts,
    updateRecording, getRecordingById,
} from '../../modules/recordingManagement/api';
import { VoiceRecording, AiProfile } from '../../modules/recordingManagement/types';
import { useSingleFileUpload, useTtsGeneration } from '../../modules/recordingManagement/hooks';
import { RecordingTtsModeSection } from './RecordingTtsModeSection';
import { RecordingUploadModeSection } from './RecordingUploadModeSection';

type RecordingMode = 'upload' | 'tts';

interface RecordingTtsModalProps {
    open: boolean;
    onClose: () => void;
    onSaved: (recording: VoiceRecording) => void;
    editRecordingId?: string | number | null;
    initialName?: string;
}

export default function RecordingTtsModal({ open, onClose, onSaved, editRecordingId, initialName = '' }: RecordingTtsModalProps) {
    const { showSuccess, showError } = useNotifications();
    const { selectedTenant } = useContextStore();

    const isEditMode = Boolean(editRecordingId);

    // ── State ─────────────────────────────────────────────────
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [mode, setMode] = useState<RecordingMode>('tts');
    const [ttsText, setTtsText] = useState('');
    const [selectedAiProfile, setSelectedAiProfile] = useState<AiProfile | null>(null);
    const [ttsProfiles, setTtsProfiles] = useState<AiProfile[]>([]);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [startSilenceMs, setStartSilenceMs] = useState(50);
    const [endSilenceMs, setEndSilenceMs] = useState(100);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [existingRecording, setExistingRecording] = useState<VoiceRecording | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const fileUpload = useSingleFileUpload();
    const tts = useTtsGeneration();

    // ── Load TTS profiles ─────────────────────────────────────
    const loadTtsProfiles = useCallback(async () => {
        try {
            const res = await fetch(buildApiUrl('/api/ai/profiles?type=tts&status=true'), {
                headers: getApiHeaders(selectedTenant?.slug || 'main'),
            });
            if (!res.ok) return;
            const profiles: AiProfile[] = (await res.json()).data || [];
            setTtsProfiles(profiles);
            if (profiles.length > 0 && !selectedAiProfile) setSelectedAiProfile(profiles[0]);
        } catch { /* silent */ }
    }, [selectedTenant, selectedAiProfile]);

    // ── Load existing recording ───────────────────────────────
    const loadExistingRecording = useCallback(async (recId: string | number) => {
        setLoadingRecord(true);
        try {
            const rec = await getRecordingById(recId);
            setExistingRecording(rec);
            setName(rec.name || '');
            setDescription(rec.description || '');
            setStartSilenceMs(rec.start_silence_ms ?? 50);
            setEndSilenceMs(rec.end_silence_ms ?? 100);
            setMode(rec.is_ai_generated ? 'tts' : 'upload');
            if (rec.is_ai_generated) setTtsText(rec.tts_text || '');
            try {
                const blob = await fetch(buildApiUrl(`/api/telephony/recordings/${recId}/play`), { headers: getApiHeaders() }).then((r) => r.blob());
                setAudioUrl(URL.createObjectURL(blob));
            } catch { /* no audio preview */ }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) { showError(err.message || 'Failed to load recording'); }
        finally { setLoadingRecord(false); }
    }, [showError]);

    // ── Init on open ──────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        setName(initialName); setDescription(''); setMode('tts'); setTtsText('');
        setAudioUrl(null); setStartSilenceMs(50); setEndSilenceMs(100); setExistingRecording(null);
        fileUpload.removeFile(); tts.clearPreview();
        loadTtsProfiles();
        if (editRecordingId) loadExistingRecording(editRecordingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editRecordingId]);

    useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

    const currentAudioUrl = fileUpload.filePreview || tts.ttsPreview || audioUrl || undefined;

    // ── Play ──────────────────────────────────────────────────
    const handlePlayAudio = useCallback(() => {
        if (!currentAudioUrl) return;
        audioRef.current?.pause();
        audioRef.current = null;
        const audio = new Audio(currentAudioUrl);
        audioRef.current = audio;
        audio.onended = () => { audioRef.current = null; };
        audio.play().catch(() => {});
    }, [currentAudioUrl]);

    // ── Save ──────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        if (!name.trim()) { showError('Name is required'); return; }
        if (mode === 'upload' && !fileUpload.selectedFile && !isEditMode) { showError('Please select an audio file'); return; }
        if (mode === 'tts' && !ttsText.trim()) { showError('Please enter text to convert'); return; }
        if (mode === 'tts' && !selectedAiProfile) { showError('Please select an AI Profile'); return; }

        setIsSaving(true);
        try {
            let result: VoiceRecording;
            if (isEditMode && editRecordingId) {
                result = await updateRecording(editRecordingId, {
                    name: name.trim(), description: description.trim() || undefined,
                    file: fileUpload.selectedFile || undefined,
                    text: mode === 'tts' ? ttsText : undefined,
                    ai_profile_id: mode === 'tts' ? selectedAiProfile?.id : undefined,
                    start_silence_ms: mode === 'tts' ? startSilenceMs : undefined,
                    end_silence_ms: mode === 'tts' ? endSilenceMs : undefined,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any);
            } else if (mode === 'upload' && fileUpload.selectedFile) {
                result = await createRecordingFromFile({ name: name.trim(), description: description.trim() || undefined, category: 'ivr', language: 'en', file: fileUpload.selectedFile });
            } else if (mode === 'tts' && selectedAiProfile) {
                result = await createRecordingFromTts({ name: name.trim(), description: description.trim() || undefined, category: 'ivr', language: 'en', text: ttsText, ai_profile_id: selectedAiProfile.id, start_silence_ms: startSilenceMs, end_silence_ms: endSilenceMs });
            } else { throw new Error('Invalid state'); }
            showSuccess(isEditMode ? 'Recording updated successfully' : 'Recording created successfully');
            onSaved(result); onClose();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) { showError(err.message || 'Failed to save recording'); }
        finally { setIsSaving(false); }
    }, [name, description, mode, ttsText, selectedAiProfile, startSilenceMs, endSilenceMs, fileUpload.selectedFile, isEditMode, editRecordingId, showSuccess, showError, onSaved, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Mic className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-foreground leading-tight">{isEditMode ? 'Edit Recording' : 'New Recording'}</h2>
                            <p className="text-xs text-muted-foreground">{isEditMode ? 'Update name, text or file for this recording' : 'Create a new recording via TTS or file upload'}</p>
                        </div>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
                    {loadingRecord ? (
                        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : (
                        <>
                            {/* Name + Description */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor="rm-name" required>Name</Label>
                                    <Input id="rm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="My greeting recording" autoFocus />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="rm-desc">Description</Label>
                                    <Input id="rm-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
                                </div>
                            </div>

                            {/* Mode Toggle (create only) */}
                            {!isEditMode && (
                                <div className="flex gap-2">
                                    <Button type="button" variant={mode === 'tts' ? 'default' : 'outline'} size="sm" onClick={() => setMode('tts')} className="flex items-center gap-2" disabled={ttsProfiles.length === 0}>
                                        <Bot className="h-4 w-4" /> AI / TTS
                                        {ttsProfiles.length === 0 && <span className="text-[10px] opacity-60 ml-1">(no profiles)</span>}
                                    </Button>
                                    <Button type="button" variant={mode === 'upload' ? 'default' : 'outline'} size="sm" onClick={() => setMode('upload')} className="flex items-center gap-2">
                                        <Upload className="h-4 w-4" /> Upload File
                                    </Button>
                                </div>
                            )}

                            {/* Mode sections */}
                            {mode === 'tts' && (
                                <RecordingTtsModeSection
                                    ttsText={ttsText} ttsProfiles={ttsProfiles} selectedAiProfile={selectedAiProfile}
                                    startSilenceMs={startSilenceMs} endSilenceMs={endSilenceMs}
                                    currentAudioUrl={currentAudioUrl} recordingName={name} tts={tts}
                                    onTtsTextChange={setTtsText} onProfileChange={setSelectedAiProfile}
                                    onStartSilenceChange={setStartSilenceMs} onEndSilenceChange={setEndSilenceMs}
                                    onAudioGenerated={setAudioUrl} onPlay={handlePlayAudio}
                                />
                            )}
                            {mode === 'upload' && (
                                <RecordingUploadModeSection
                                    isEditMode={isEditMode} existingRecording={existingRecording}
                                    currentAudioUrl={currentAudioUrl} fileUpload={fileUpload} onPlay={handlePlayAudio}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border flex-shrink-0 bg-muted/30">
                    <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button type="button" size="sm" onClick={handleSave} disabled={isSaving || loadingRecord} className="flex items-center gap-2">
                        {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Mic className="h-4 w-4" /> {isEditMode ? 'Update Recording' : 'Save Recording'}</>}
                    </Button>
                </div>
            </div>
        </div>
    );
}
